/**
 * Push subscription lifecycle state machine.
 *
 * ## States
 *
 * ```
 * unsubscribed
 *    │  subscribeStart
 *    ▼
 * subscribing
 *    │  subscribeSuccess(record)
 *    ▼
 * subscribed ──────────────────────────┐
 *    │  pushReceived(now, record) or   │
 *    │  expiryWarning                  │ (either from subscribed or expiring)
 *    ▼                                 │
 * expiring ─────────────────────────── ┤
 *    │  unsubscribe                    │
 *    ▼                                 │
 * unsubscribed ◄───────────────────────┘
 * ```
 *
 * Additional transitions:
 * - `subscribeFail` from `subscribing` → `unsubscribed`
 * - `unsubscribe` from any state → `unsubscribed`
 * - `pushReceived` when `record.expirationTime - now < EXPIRY_WARNING_THRESHOLD_MS`
 *   drives directly into `expiring` (combines the push-received event with the
 *   expiry check in one step).
 *
 * ## Expiration semantics
 *
 * A `PushSubscriptionRecord` may carry an optional `expirationTime`
 * (milliseconds since Unix epoch, matching the Web Push API's
 * `PushSubscription.expirationTime`).  If `expirationTime - now` is less
 * than {@link EXPIRY_WARNING_THRESHOLD_MS}, the state transitions to
 * `expiring`.  An expiring subscription is still usable (the push service
 * will deliver messages) but the application should attempt a fresh
 * subscription at the next opportunity.
 *
 * Pure-function — no side effects. No Service Worker or pushManager calls.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Possible lifecycle states for a push subscription. */
export type PushSubscriptionState =
  | "unsubscribed"
  | "subscribing"
  | "subscribed"
  | "expiring";

/**
 * A push subscription record as stored by the application.
 *
 * `expirationTime` is `null` when the push service reports no expiration
 * (matches `PushSubscription.expirationTime` semantics).
 */
export interface PushSubscriptionRecord {
  /** The push endpoint URL, already validated by PushSubscriptionValidator. */
  readonly endpoint: string;
  /**
   * Expiration timestamp in milliseconds since Unix epoch, or `null` if the
   * subscription does not expire.
   */
  readonly expirationTime: number | null;
}

/** Union of all events that drive the state machine. */
export type PushSubscriptionEvent =
  | { readonly type: "subscribeStart" }
  | { readonly type: "subscribeSuccess"; readonly record: PushSubscriptionRecord }
  | { readonly type: "subscribeFail"; readonly error: string }
  | {
      readonly type: "pushReceived";
      /** Current wall-clock time as `Date.now()` (ms since epoch). */
      readonly now: number;
      readonly record: PushSubscriptionRecord;
    }
  | { readonly type: "expiryWarning" }
  | { readonly type: "unsubscribe" };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * If the remaining lifetime of a subscription is below this threshold the
 * state advances to `expiring`.
 *
 * Set to 7 days (in milliseconds) — the same budget used by Chrome's push
 * service to send its own expiry warnings.
 */
export const EXPIRY_WARNING_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

/**
 * Return `true` if `record` is expiring soon relative to `now`.
 */
function isExpiringSoon(record: PushSubscriptionRecord, now: number): boolean {
  if (record.expirationTime === null) return false;
  return record.expirationTime - now < EXPIRY_WARNING_THRESHOLD_MS;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Pure-function state transition.
 *
 * Given the current `state` and an incoming `event`, returns the next state.
 * Illegal combinations (e.g. `subscribeSuccess` when already `subscribed`)
 * are silently ignored and the current state is returned unchanged — the
 * machine is tolerant of duplicate or out-of-order events.
 */
export function transition(
  state: PushSubscriptionState,
  event: PushSubscriptionEvent,
): PushSubscriptionState {
  // `unsubscribe` is valid from any state and always resets to `unsubscribed`.
  if (event.type === "unsubscribe") {
    return "unsubscribed";
  }

  switch (state) {
    case "unsubscribed": {
      if (event.type === "subscribeStart") return "subscribing";
      return state;
    }

    case "subscribing": {
      if (event.type === "subscribeSuccess") {
        // Check whether the just-obtained subscription is already near expiry.
        return isExpiringSoon(event.record, Date.now())
          ? "expiring"
          : "subscribed";
      }
      if (event.type === "subscribeFail") return "unsubscribed";
      return state;
    }

    case "subscribed": {
      if (event.type === "pushReceived") {
        return isExpiringSoon(event.record, event.now)
          ? "expiring"
          : "subscribed";
      }
      if (event.type === "expiryWarning") return "expiring";
      return state;
    }

    case "expiring": {
      // An expiring subscription can still receive pushes.
      if (event.type === "pushReceived") {
        // Re-check in case a renewed subscription snuck in — if no longer
        // expiring, promote back to subscribed.
        return isExpiringSoon(event.record, event.now)
          ? "expiring"
          : "subscribed";
      }
      // subscribeStart can be issued to renew from the expiring state.
      if (event.type === "subscribeStart") return "subscribing";
      return state;
    }
  }
}
